import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { specializationService, CreateSpecializationPayload } from '../specializationService';

/** All specializations (used for dropdowns/selects). */
export function useSpecializations() {
  return useQuery({
    queryKey: ['specializations', 'all'],
    queryFn: () => specializationService.getAllSpecializations(),
    staleTime: 30000,
  });
}

/** Paginated + searchable specializations (used by the list page). */
export function usePaginatedSpecializations(page = 1, limit = 20, search?: string) {
  return useQuery({
    queryKey: ['specializations', 'paginated', page, limit, search],
    queryFn: () => specializationService.getSpecializations(page, limit, search),
    placeholderData: (previous) => previous,
    staleTime: 30000,
  });
}

export function useActiveSpecializations() {
  return useQuery({
    queryKey: ['specializations', 'active'],
    queryFn: () => specializationService.getActiveSpecializations(),
    staleTime: 30000,
  });
}

export function useCreateSpecialization() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateSpecializationPayload) => specializationService.createSpecialization(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['specializations'] });
    },
  });
}

export function useUpdateSpecialization() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CreateSpecializationPayload> }) =>
      specializationService.updateSpecialization(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['specializations'] });
    },
  });
}

export function useDeleteSpecialization() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => specializationService.deleteSpecialization(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['specializations'] });
    },
  });
}

/** Bulk-delete specializations as a single silent mutation (loops the single-delete endpoint). */
export function useBulkDeleteSpecializations() {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { hideToast: true },
    mutationFn: async (ids: string[]) => {
      for (const id of ids) {
        await specializationService.deleteSpecialization(id);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['specializations'] });
    },
  });
}
